import React from "react";
import { toast } from "react-toastify";
import { ethers } from "ethers";
import {
  setTxConfirmed,
  setTxFailed,
  setTxPending,
} from "../../ReduxSlices/ModalSlice";
import { formatErrorForDisplay, getErrorIcon, getErrorColor } from "../../Components/functions/ErrorHandler";
import { setPerpsUpdate } from "../../ReduxSlices/TradeSlice";
import {
  PERP_VAULT_INTERFACE_ABI,
  RENTAL_EVM_INTERFACE_ABI,
  RENTAL_EVM_INTERFACE_ADDRESS,
  NIBIRU_EVM_NETWORK_NAME,
  NIBIRU_EVM_CHAIN,
} from "../EvmConstants";
import {
  checkAndApproveUSDC,
  parseTradeIdFromReceipt,
  fetchEvmTokenBalance,
} from "./tradingEvmService";
import FUNTOKEN_ABI from "@nibiruchain/solidity/abi/IFunToken.json";

const getTransactionLink = (txHash) => {
  const base = (NIBIRU_EVM_CHAIN.blockExplorerUrls && NIBIRU_EVM_CHAIN.blockExplorerUrls[0]) || "https://nibiscan.io";
  const url = base.replace(/\/$/, "");
  return `${url}/tx/${txHash}`;
};

/**
 * Get the EVM provider to use for transactions.
 * When multiple wallets inject (e.g. MetaMask + Trust Wallet), prefer MetaMask
 * so the wallet the user connected with is the one that signs.
 */
export const getEthereumProvider = () => {
  if (typeof window === "undefined" || !window.ethereum) return null;
  const eth = window.ethereum;
  if (eth.providers?.length) {
    const metamask = eth.providers.find((p) => p.isMetaMask);
    if (metamask) return metamask;
  }
  return eth;
};

/**
 * EVM-only contract execution for general functions
 * @param {Object} dispatch - Redux dispatch function
 * @param {Object} CONTRACT_MESSAGE - Wasm ExecuteMsg JSON to execute
 * @param {string} account - User's EVM address
 * @param {Array} tokenToSend - Optional funds metadata for reservation payments
 * @param {Array} funds - Optional Cosmos-style funds [{ denom, amount }] for executeRentalWithFunds (e.g. mint fee)
 * @returns {Promise<string|false>} Transaction hash or false
 */
export const executeEvmContract = async (
  dispatch,
  CONTRACT_MESSAGE,
  account,
  tokenToSend = null,
  funds = null
) => {
  try {
    validateDispatch(dispatch);
    const ethereumProvider = getEthereumProvider();
    if (!ethereumProvider) {
      toast.error("No EVM wallet found. Install MetaMask or connect your wallet.");
      return false;
    }

    toast.loading("Transaction is pending...");
    dispatch(setTxPending());

    // Switch to Nibiru EVM so the correct wallet signs on the correct network
    const switched = await switchToNibiruEvm(ethereumProvider);
    if (!switched) {
      toast.warning(`Please switch your wallet to ${NIBIRU_EVM_NETWORK_NAME} to continue.`);
      dispatch(setTxFailed());
      return false;
    }

    console.log("Using EVM wallet with account:", account);

    const provider = new ethers.BrowserProvider(ethereumProvider);
    const signer = await provider.getSigner();

    const contractAddress = RENTAL_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      throw new Error(
        "Missing rental EVM gateway address. Please set REACT_APP_NIBIRU_EVM_RENTAL_CONTRACT_ADDRESS (or REACT_APP_NIBIRU_EVM_CONTRACT_ADDRESS) in your .env file."
      );
    }

    // Rental gateway contract instance
    const contract = new ethers.Contract(
      contractAddress,
      RENTAL_EVM_INTERFACE_ABI,
      signer
    );

    // Convert Wasm ExecuteMsg JSON to UTF-8 bytes
    const wasmMsgExecute = ethers.toUtf8Bytes(JSON.stringify(CONTRACT_MESSAGE));
    const messageType = Object.keys(CONTRACT_MESSAGE || {})[0] || "unknown";

    let hash;

    /**
     * Case 1: Reservation with Payment (USDC ERC20)
     */
    if (
      messageType === "set_reservation_for_short_term" &&
      tokenToSend &&
      tokenToSend.length > 0
    ) {
      const rawAmount = tokenToSend[0].amount;
      const usdcDecimals = Number(process.env.REACT_APP_USDC_DECIMALS ?? "6") || 6;
      const amountStr = String(rawAmount ?? "").trim();

      // Prefer exact decimal parse from string (e.g. "0.030900") so fee isn't lost to float/2dp rounding
      let usdcAmountBigInt;
      try {
        usdcAmountBigInt = ethers.parseUnits(amountStr, usdcDecimals);
      } catch {
        const humanReadableAmount = parseFloat(amountStr);
        if (!humanReadableAmount || humanReadableAmount <= 0 || isNaN(humanReadableAmount)) {
          throw new Error(`Invalid reservation amount: ${rawAmount}.`);
        }
        const usdcAmountFloat = humanReadableAmount * Math.pow(10, usdcDecimals);
        const usdcAmountRounded = Math.ceil(usdcAmountFloat - 1e-12);
        if (usdcAmountRounded <= 0) {
          throw new Error(`Reservation amount too small: ${humanReadableAmount} USDC (min 0.000001).`);
        }
        // eslint-disable-next-line no-undef
        usdcAmountBigInt = BigInt(usdcAmountRounded);
      }

      if (usdcAmountBigInt <= 0n) {
        throw new Error(`Invalid reservation amount: ${rawAmount}.`);
      }

      const humanReadableAmount = parseFloat(ethers.formatUnits(usdcAmountBigInt, usdcDecimals));
      if (!humanReadableAmount || humanReadableAmount <= 0 || isNaN(humanReadableAmount)) {
        throw new Error(`Invalid reservation amount: ${rawAmount}.`);
      }

      const usdcContractAddress = process.env.REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS;
      if (!usdcContractAddress) {
        throw new Error("Missing REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS.");
      }

      // Prompt user to add USDC to wallet if not already visible (Disabled by user request - too repetitive)
      /*try {
        await ethereumProvider.request({
          method: "wallet_watchAsset",
          params: {
            type: "ERC20",
            options: {
              address: usdcContractAddress,
              symbol: "USDC.e",
              decimals: 6,
              image: "https://cryptologos.cc/logos/usd-coin-usdc-logo.png",
            },
          },
        });
      } catch (e) {
        console.warn("Failed to add USDC token to wallet:", e);
      }*/

      // 1. Check & Approve USDC for the Rental Gateway
      // Note: checkAndApproveUSDC expects amount in human-readable USDC (float)
      const approved = await checkAndApproveUSDC(
        account,
        contractAddress, // Rental Gateway needs approval
        humanReadableAmount
      );

      if (!approved) {
        throw new Error("USDC approval failed or was rejected.");
      }

      // Clone and modify the message to include is_evm_origin flag
      // This tells the Wasm contract that the request comes from EVM Gateway
      const executeMsg = JSON.parse(JSON.stringify(CONTRACT_MESSAGE));
      // Ensure guests is an integer to prevent Wasm parsing errors
      if (executeMsg.set_reservation_for_short_term.guests) {
        executeMsg.set_reservation_for_short_term.guests = parseInt(executeMsg.set_reservation_for_short_term.guests);
      }

      const wasmMsgExecuteWithFlag = ethers.toUtf8Bytes(JSON.stringify(executeMsg));

      // Pass explicit bank denom so gateway can attach Cosmos funds expected by the Wasm contract.
      const paymentDenomParam =
        process.env.REACT_APP_USDC_DENOM || "erc20/0x0829F361A05D993d5CEb035cA6DF3446b060970b";

      console.log("Rental reservation via EVM gateway (ERC20 path):", {
        account,
        humanReadableAmount,
        usdcAmountBigInt: usdcAmountBigInt.toString(),
        usdcContractAddress,
        message: executeMsg,
        paymentDenomParam: paymentDenomParam || "(derived from ERC20)",
        paymentAmountBank: usdcAmountBigInt.toString(),
        useErc20Amount: usdcAmountBigInt.toString()
      });

      // 2. Execute setReservationForShortTerm using ERC20 payment
      const tx = await contract.setReservationForShortTerm(
        wasmMsgExecuteWithFlag,
        paymentDenomParam,
        usdcAmountBigInt,           // paymentAmountBank: Must be > 0 (validated by contract)
        usdcContractAddress,        // paymentErc20 address
        usdcAmountBigInt,           // useErc20Amount
        { gasLimit: 15_000_000n }
      );

      hash = tx.hash;
      await tx.wait();

    } else if (funds && funds.length > 0) {
      /**
       * Case 2: Actions requiring Native Funds (e.g. Minting fee in UNIBI)
       */
      // ... existing logic for funds ...
      /* eslint-disable no-undef */
      const fundsStruct = funds.map((f) => ({
        denom: f.denom || "unibi",
        amount: typeof f.amount === "string" ? BigInt(f.amount) : BigInt(Number(f.amount)),
      }));
      /* eslint-enable no-undef */

      const iface = contract.interface;
      const calldata = iface.encodeFunctionData("executeRentalWithFunds", [
        wasmMsgExecute,
        fundsStruct,
      ]);

      const txRequest = {
        to: contractAddress,
        data: calldata,
        gasLimit: 3_000_000n,
      };

      const tx = await signer.sendTransaction(txRequest);
      hash = tx.hash;
      await tx.wait();

    } else {
      /**
       * Case 3: Generic Execution (No funds)
       */
      const tx = await contract.executeRental(wasmMsgExecute, {
        gasLimit: 3_000_000n,
      });

      hash = tx.hash;
      await tx.wait();
    }

    dispatch(setTxConfirmed());
    toast.dismiss();
    toast.success(
      <div>
        Transaction confirmed!
        <br />
        <a
          href={`https://${getTransactionLink(hash)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          View transaction
        </a>
      </div>
    );

    return hash;
  } catch (error) {
    if (dispatch && typeof dispatch === "function") {
      dispatch(setTxFailed());
    }
    const errorText = error.toString();
    console.error("EVM rental execution error:", errorText);
    toast.dismiss();

    const errorData = formatErrorForDisplay(errorText);
    const icon = getErrorIcon(errorData.category);
    const color = getErrorColor(errorData.category);

    toast.error(
      <div className="flex items-start gap-3">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `${color}20`, color }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Transaction Failed</div>
          <div className="text-sm mt-1">{errorData.message}</div>
        </div>
      </div>,
      {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    );

    return false;
  }
};

/**
 * EVM-only perp trading execution
 * @param {Object} dispatch - Redux dispatch function
 * @param {Object} CONTRACT_MESSAGE - Wasm message to execute
 * @param {string} account - User's EVM address
 * @param {Array} tokenToSend - Array of tokens to send
 * @returns {Promise<string|false>} Transaction hash or false
 */
export const executeEvmPerps = async (
  dispatch,
  CONTRACT_MESSAGE,
  account,
  tokenToSend
) => {
  try {
    validateDispatch(dispatch);
    if (!window.ethereum) {
      toast.error("MetaMask is not installed");
      return false;
    }

    toast.loading("Transaction is pending...");
    dispatch(setTxPending());

    console.log("Using EVM wallet for perps trading:", account);

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_NIBIRU_EVM_CONTRACT_ADDRESS;
    if (!contractAddress) {
      throw new Error("Missing REACT_APP_NIBIRU_EVM_CONTRACT_ADDRESS environment variable");
    }

    // Validate required parameters
    const microUsdcAmount = parseFloat(tokenToSend[0].amount); // This is already in micro-USDC
    const usdcAmount = microUsdcAmount / 1000000; // Convert to USDC for display and approval

    if (usdcAmount <= 0) {
      throw new Error("Trade amount must be greater than 0");
    }

    console.log("Trade amount validation:", {
      microUsdcAmount,
      usdcAmount,
      originalAmount: tokenToSend[0].amount
    });

    // Check current ERC20 USDC balance
    const currentBalance = await fetchEvmTokenBalance(account);
    console.log("Current ERC20 USDC balance:", currentBalance);

    if (currentBalance < usdcAmount) {
      toast.error(`Insufficient USDC balance. Required: ${usdcAmount}, Available: ${currentBalance}`);
      return false;
    }

    // Check and approve USDC first
    const approved = await checkAndApproveUSDC(account, contractAddress, usdcAmount);
    if (!approved) {
      throw new Error("USDC approval failed");
    }

    // Create contract instance
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    console.log("Executing perp trade with message:", JSON.stringify(CONTRACT_MESSAGE, null, 2));

    // Convert Wasm message to bytes
    const wasmMsgExecute = ethers.toUtf8Bytes(JSON.stringify(CONTRACT_MESSAGE));

    // Configure collateral amounts properly
    const totalCollateralAmount = ethers.parseUnits(usdcAmount.toString(), 6); // Total collateral needed (in micro-USDC)
    const erc20CollateralAmount = totalCollateralAmount; // Use all from ERC20 balance
    const bankCollateralAmount = 0; // No bank balance used for now

    // Get collateral index from environment
    const collateralIndex = parseInt(process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL?.replace(/[^\d]/g, '') || '3');

    console.log("Collateral configuration:", {
      collateralIndex,
      totalCollateral: totalCollateralAmount.toString(),
      erc20Collateral: erc20CollateralAmount.toString(),
      bankCollateral: bankCollateralAmount.toString(),
      difference: (totalCollateralAmount - erc20CollateralAmount).toString()
    });

    console.log("Final transaction parameters:", {
      wasmMessage: JSON.stringify(CONTRACT_MESSAGE),
      collateralIndex,
      totalCollateralAmount: totalCollateralAmount.toString(),
      erc20CollateralAmount: erc20CollateralAmount.toString(),
      contractAddress,
      account
    });

    // Execute the trade using openTrade function with proper collateral configuration
    const tx = await contract.openTrade(
      wasmMsgExecute,           // Wasm message as bytes
      collateralIndex,          // collateralIndex (USDC = 3)
      totalCollateralAmount,    // totalCollateralAmount (total collateral needed)
      erc20CollateralAmount,    // erc20CollateralAmount (amount from ERC20)
      {
        gasLimit: 5000000       // Set higher gas limit for complex operations
      }
    );

    console.log("Transaction sent:", tx.hash);

    // Wait for confirmation
    const receipt = await tx.wait();
    console.log("Transaction confirmed:", receipt);

    // Parse trade ID from receipt
    const tradeId = parseTradeIdFromReceipt(receipt);
    console.log("Parsed trade ID:", tradeId);

    dispatch(setTxConfirmed());
    toast.dismiss();
    toast.success(
      <div>
        Transaction confirmed!
        <br />
        <a
          href={`https://${getTransactionLink(receipt.hash)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          View transaction
        </a>
      </div>
    );
    dispatch(setPerpsUpdate());

    return receipt.hash;
  } catch (error) {
    if (dispatch && typeof dispatch === 'function') {
      dispatch(setTxFailed());
    }
    toast.dismiss();

    const errorMessage = error.toString().toLowerCase();

    // EVM-specific error handling for perps
    if (
      errorMessage.includes("insufficient funds") ||
      errorMessage.includes("out of gas")
    ) {
      toast.error(
        "Insufficient balance to complete this transaction. Please add funds to your wallet."
      );
    } else if (
      errorMessage.includes("nonce") ||
      errorMessage.includes("transaction count")
    ) {
      toast.error("Transaction nonce error. Please try again in a moment.");
    } else if (
      errorMessage.includes("chain id") ||
      errorMessage.includes("network") ||
      errorMessage.includes("wrong network")
    ) {
      toast.error(
        "Network connection issue. Please check your wallet is connected to Nibiru EVM."
      );
    } else if (
      errorMessage.includes("rejected") ||
      errorMessage.includes("declined") ||
      errorMessage.includes("cancelled") ||
      errorMessage.includes("user rejected")
    ) {
      toast.error("Transaction was declined. Please try again.");
    } else if (
      errorMessage.includes("timeout") ||
      errorMessage.includes("timed out")
    ) {
      toast.error(
        "Transaction timed out. The network might be congested, please try again."
      );
    } else if (
      errorMessage.includes("not connected") ||
      errorMessage.includes("no provider")
    ) {
      toast.error(
        "Wallet connection lost. Please reconnect your wallet and try again."
      );
    } else if (errorMessage.includes("slippage")) {
      toast.error(
        "Price changed during transaction. Try again with a higher slippage tolerance."
      );
    } else if (errorMessage.includes("limit") || errorMessage.includes("max")) {
      toast.error(
        "This transaction exceeds platform limits. Please try a smaller amount."
      );
    } else if (errorMessage.includes("insufficient allowance")) {
      toast.error(
        "USDC allowance insufficient. Please approve USDC spending and try again."
      );
    } else {
      toast.error("Unable to process transaction. Please try again.");
    }
    console.error(`EVM Transaction error details: ${error.toString()}`);
    return false;
  }
};

/**
 * Query EVM contract (read-only operations)
 * @param {string} contractAddr - Contract address
 * @param {Object} CONTRACT_MESSAGE - Query message
 * @returns {Promise<any>} Query result
 */
export const queryEvmContract = async (contractAddr, CONTRACT_MESSAGE) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const contract = new ethers.Contract(contractAddr, PERP_VAULT_INTERFACE_ABI, provider);

    // Convert message to bytes for query
    const wasmMsgQuery = ethers.toUtf8Bytes(JSON.stringify(CONTRACT_MESSAGE));

    // Execute query (this would need a query function in the contract)
    // Note: This assumes the contract has a query function
    const result = await contract.queryContract(wasmMsgQuery);

    return result;
  } catch (error) {
    console.error("EVM contract query error:", error);
    throw error;
  }
};

/**
 * Get EVM token balance (USDC)
 * @param {string} account - User's EVM address
 * @returns {Promise<Object>} Balance object
 */
export const queryEvmBalance = async (account) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const usdcContractAddress = process.env.REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS;

    if (!usdcContractAddress) {
      throw new Error("USDC contract address not configured");
    }

    // Get USDC balance
    const balance = await provider.call({
      to: usdcContractAddress,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    const balanceDecoded = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0];
    const formattedBalance = ethers.formatUnits(balanceDecoded, 6); // USDC has 6 decimals

    return {
      amount: (Number(formattedBalance) * 1000000).toString(), // Convert to micro-USDC for consistency
      denom: process.env.REACT_APP_COLLATERAL_DENOM || "usdc" // Use env denom or fallback
    };
  } catch (error) {
    console.error("Error fetching EVM balance:", error);
    return { amount: "0", denom: process.env.REACT_APP_COLLATERAL_DENOM || "usdc" };
  }
};

/**
 * Get current price from EVM oracle (if available)
 * Note: This might need to be implemented based on available oracle functions
 * @returns {Promise<number|null>} Current price or null
 */
export const getEvmCurrentPrice = async () => {
  try {
    // For now, this might need to fall back to the Cosmos oracle
    // or implement EVM-specific oracle queries
    console.log("EVM oracle price query not yet implemented");
    return null;
  } catch (err) {
    console.error("Failed to fetch current price from EVM oracle:", err);
    return null;
  }
};

/**
 * Fetches all trades for a given EVM account
 * Note: This would require the contract to have trade query functions
 * @param {string} account - EVM account address
 * @returns {Promise<Array>} Array of trades
 */
export const getEvmTrades = async (account) => {
  try {
    console.log("EVM trades query not yet implemented for account:", account);
    // This would need to be implemented based on available contract functions
    return [];
  } catch (err) {
    console.error("Failed to fetch EVM trades:", err);
    return [];
  }
};

/**
 * Utility function to convert Cosmos address to EVM address (if needed)
 * @param {string} cosmosAddress - Cosmos bech32 address
 * @returns {string} EVM hex address
 */
export const cosmosToEvmAddress = (cosmosAddress) => {
  try {
    // This would need proper address conversion logic
    // For now, return as-is if it's already EVM format
    if (cosmosAddress.startsWith('0x')) {
      return cosmosAddress;
    }

    // Implement proper conversion if needed
    console.warn("Cosmos to EVM address conversion not implemented");
    return cosmosAddress;
  } catch (error) {
    console.error("Error converting address:", error);
    return cosmosAddress;
  }
};

/**
 * Convert EVM address to Cosmos bech32 address using funtoken precompile
 * @param {string} evmAddress - EVM hex address (0x...)
 * @returns {Promise<string>} Cosmos bech32 address (nibi...)
 */
export const evmToBech32Address = async (evmAddress) => {
  try {
    console.log("🔄 Starting EVM to bech32 conversion for:", evmAddress);

    const ethProvider = getEthereumProvider();
    if (!ethProvider) {
      throw new Error("No EVM wallet found");
    }

    if (!evmAddress || !evmAddress.startsWith('0x')) {
      throw new Error("Invalid EVM address format");
    }

    const provider = new ethers.BrowserProvider(ethProvider);

    // Funtoken precompile contract address
    const FUNTOKEN_PRECOMPILE_ADDRESS = process.env.REACT_APP_NIBIRU_FUNTOKEN_PRECOMPILE_ADDRESS;


    console.log("📞 Calling funtoken precompile at:", FUNTOKEN_PRECOMPILE_ADDRESS);

    // Use the imported FUNTOKEN_ABI
    const contract = new ethers.Contract(FUNTOKEN_PRECOMPILE_ADDRESS, FUNTOKEN_ABI, provider);

    console.log("📋 Contract instance created, calling whoAmI...");

    // Call whoAmI function to get address information
    // The function expects a string parameter (can be EVM address or bech32)
    const result = await contract.whoAmI(evmAddress);

    console.log("📥 Raw whoAmI result:", result);

    // Extract bech32 address from the NibiruAccount struct
    const bech32Address = result.bech32Addr;

    console.log("✅ Address conversion successful:", {
      evmAddress,
      bech32Address,
      ethAddr: result.ethAddr,
      fullResult: result
    });

    if (!bech32Address || !bech32Address.startsWith('nibi')) {
      throw new Error(`Invalid bech32 address returned: ${bech32Address}`);
    }

    return bech32Address;
  } catch (error) {
    console.error("❌ Error converting EVM to bech32 address:", error);
    console.error("Error details:", {
      message: error.message,
      code: error.code,
      data: error.data,
      stack: error.stack
    });
    throw new Error(`Failed to convert address: ${error.message}`);
  }
};


/**
 * Get bech32 address for current EVM account
 * @returns {Promise<string>} Cosmos bech32 address
 */
export const getCurrentBech32Address = async () => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const evmAddress = await signer.getAddress();

    return await evmToBech32Address(evmAddress);
  } catch (error) {
    console.error("Error getting current bech32 address:", error);
    throw error;
  }
};

/**
 * Check if current network is Nibiru EVM
 * @returns {Promise<boolean>} True if on correct network
 */
export const checkEvmNetwork = async () => {
  try {
    if (!window.ethereum) {
      return false;
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const network = await provider.getNetwork();
    const expectedChainId = process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID;

    return network.chainId.toString() === parseInt(expectedChainId, 16).toString();
  } catch (error) {
    console.error("Error checking EVM network:", error);
    return false;
  }
};

/**
 * Switch to Nibiru EVM network on the given provider (so the correct wallet is on the right chain).
 * @param {Object} [provider] - EIP-1193 provider (default: getEthereumProvider())
 * @returns {Promise<boolean>} True if switch was successful
 */
export const switchToNibiruEvm = async (provider = null) => {
  const eth = provider ?? getEthereumProvider();
  try {
    if (!eth) {
      throw new Error("No EVM wallet found");
    }

    const chainId = NIBIRU_EVM_CHAIN.chainId;
    if (!chainId) {
      console.error("REACT_APP_NIBIRU_EVM_CHAIN_ID is not set");
      return false;
    }

    await eth.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId }],
    });

    return true;
  } catch (switchError) {
    if (switchError.code === 4902) {
      try {
        await eth.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: NIBIRU_EVM_CHAIN.chainId,
              chainName: NIBIRU_EVM_CHAIN.chainName,
              rpcUrls: NIBIRU_EVM_CHAIN.rpcUrls,
              nativeCurrency: NIBIRU_EVM_CHAIN.nativeCurrency,
              blockExplorerUrls: NIBIRU_EVM_CHAIN.blockExplorerUrls,
            },
          ],
        });
        return true;
      } catch (addError) {
        console.error("Error adding Nibiru EVM network:", addError);
        return false;
      }
    }
    console.error("Error switching to Nibiru EVM network:", switchError);
    return false;
  }
};

// Add dispatch validation to other functions
const validateDispatch = (dispatch) => {
  if (!dispatch || typeof dispatch !== 'function') {
    throw new Error("Invalid dispatch function provided");
  }
};